import type { HttpResponse, ObservedResponse, ResponseBody } from './types';

/** A body that can be read once, under a cap, plus what the counter saw. */
export interface CappedBody {
  readonly body: ResponseBody;
  /** Whether the stream reached EOF without producing a byte. */
  readonly empty: boolean;
  /**
   * The failure the counter raised, or `undefined` while the cap still holds.
   *
   * A reader can swallow the rejection it gets — a `try`/`catch` around a
   * `json()` is ordinary code — and would then hand back a value built from
   * half an answer. This is what lets the caller notice that regardless.
   */
  overflow(): Error | undefined;
}

/**
 * The cap on a response body when `maxResponseBytes` is not set: 10 MiB.
 *
 * @remarks
 * Exported so a consumer that reads a body on another route can hold it to the
 * same limit instead of restating the number. `request` reads this binding
 * whenever `maxResponseBytes` is unset, and `Infinity` or `-1` switch the cap
 * off.
 *
 * The cap applies per attempt, not across the call, as `maxResponseBytes`
 * explains.
 */
export const DEFAULT_MAX_RESPONSE_BYTES = 10 * 1024 * 1024;

/**
 * The cap in bytes, or `undefined` for "read as much as arrives".
 *
 * `Infinity` and `-1` are the two documented ways to say no cap, and every
 * other value that cannot be a byte count — a negative, a `NaN` — means the
 * same thing rather than a cap of zero that would refuse every answer.
 */
function limitOf(maxResponseBytes: number | undefined): number | undefined {
  const limit = maxResponseBytes ?? DEFAULT_MAX_RESPONSE_BYTES;
  return Number.isFinite(limit) && limit >= 0 ? limit : undefined;
}

/** What the response CLAIMS it will send, when that claim is usable at all. */
function declaredLengthOf(headers: Headers): number | undefined {
  const header = headers.get('content-length');
  if (header === null) return undefined;
  const declaredBytes = Number(header);
  return Number.isFinite(declaredBytes) && declaredBytes >= 0
    ? declaredBytes
    : undefined;
}

/**
 * Reads one response body, at most `maxResponseBytes` of it.
 *
 * The cap is enforced in two places because one is cheap and the other is
 * honest. A `content-length` over the cap is refused here, before the socket is
 * read at all, which is the only way to spend nothing on an answer we already
 * know is too big. That header is a claim though, so a counter over the stream
 * runs as well: it catches a missing length and an understated one, and it
 * breaks the read off at the chunk that crosses the line rather than draining
 * first and judging after.
 *
 * The counter sits BETWEEN the socket and the reading methods, so `json()` is
 * capped exactly as `body` is: whichever way the caller reads, the bytes pass
 * the same gate.
 *
 * `tooLarge` is injected rather than constructed here, so this module stays
 * about reading under a cap and knows nothing of the error vocabulary — the
 * same split `address.ts` and `body.ts` already follow.
 *
 * Throws `tooLarge()` on the `content-length` refusal. Everything the counter
 * catches surfaces later, as a rejection from whichever read was in progress.
 */
export async function readResponseBody(
  response: HttpResponse,
  maxResponseBytes: number | undefined,
  tooLarge: () => Error,
): Promise<CappedBody> {
  const limit = limitOf(maxResponseBytes);
  const source = response.body;

  if (limit !== undefined && source !== null) {
    const declaredBytes = declaredLengthOf(response.headers);
    if (declaredBytes !== undefined && declaredBytes > limit) {
      // Nobody is going to read this, and an unread stream holds its connection
      // open. Whether the cancel itself succeeds changes nothing about the call.
      void source.cancel().catch(() => {});
      throw tooLarge();
    }
  }

  let overflow: Error | undefined;
  let read = 0;
  const stream =
    limit === undefined || source === null
      ? source
      : source.pipeThrough(
          new TransformStream<Uint8Array, Uint8Array>({
            transform(chunk, controller) {
              read += chunk.byteLength;
              if (read > limit) {
                overflow = tooLarge();
                controller.error(overflow);
                return;
              }
              controller.enqueue(chunk);
            },
          }),
        );

  // A real `Response` does the reading. That is where the parity `ResponseBody`
  // promises comes from — the same results, the same "already read" refusal on
  // a second call — instead of six reimplementations that drift from it. The
  // headers travel with it because `formData()` and `blob()` read the
  // content-type to do their work.
  const probed = await probeBody(stream);
  const reader = new Response(probed.stream, { headers: response.headers });

  return {
    body: {
      arrayBuffer: () => reader.arrayBuffer(),
      blob: () => reader.blob(),
      // The stream the counter guards, not the socket: a caller that reads
      // chunk by chunk passes the same gate as one that calls `text()`.
      get body() {
        return probed.stream;
      },
      get bodyUsed() {
        return reader.bodyUsed;
      },
      bytes: () => reader.bytes(),
      formData: () => reader.formData(),
      json: () => reader.json(),
      text: () => reader.text(),
    },
    empty: probed.empty,
    overflow: () => overflow,
  };
}

/** Inspect one byte and replay it without buffering the rest of the response. */
async function probeBody(
  stream: ReadableStream<Uint8Array> | null,
): Promise<{ empty: boolean; stream: ReadableStream<Uint8Array> | null }> {
  if (stream === null) return { empty: true, stream: null };
  const reader = stream.getReader();
  let first: Uint8Array;
  try {
    for (;;) {
      const step = await reader.read();
      if (step.done) {
        reader.releaseLock();
        return { empty: true, stream: null };
      }
      if (step.value.byteLength > 0) {
        first = step.value;
        break;
      }
    }
  } catch (error) {
    reader.releaseLock();
    throw error;
  }

  let pending: Uint8Array | undefined = first;
  return {
    empty: false,
    stream: new ReadableStream<Uint8Array>(
      {
        async cancel(reason) {
          try {
            await reader.cancel(reason);
          } finally {
            reader.releaseLock();
          }
        },
        async pull(controller) {
          if (pending !== undefined) {
            controller.enqueue(pending);
            pending = undefined;
            return;
          }
          try {
            const step = await reader.read();
            if (step.done) {
              reader.releaseLock();
              controller.close();
            } else {
              controller.enqueue(step.value);
            }
          } catch (error) {
            reader.releaseLock();
            controller.error(error);
          }
        },
      },
      { highWaterMark: 0 },
    ),
  };
}

/**
 * Closes a body nobody read.
 *
 * A `deserializer` may decide it needs none of the answer, and an unread stream
 * keeps its connection open until something times out. Left alone when it was
 * read, and when it is locked — a caller holding a reader owns it, and
 * cancelling under them would throw.
 */
export async function releaseBody(body: ResponseBody): Promise<void> {
  const stream = body.body;
  if (stream === null || body.bodyUsed || stream.locked) return;
  await stream.cancel().catch(() => {});
}

/**
 * The response without its stream, rebuilt rather than handed over.
 *
 * It lives HERE, beside the pipe that makes the original unusable: this module
 * puts a counting `TransformStream` between the socket and every reader, so the
 * stream on the response is locked from that moment on and the readable one is a
 * different object.
 *
 * Two callers need this view and must not drift apart on it. An `after` handler
 * must not be able to steal the stream the caller owns (`AF8`, `AF9`), and a
 * deserializer must not reach for one that is already piped (`RB12`). Handing
 * over the response itself would leave the field one property away in both
 * cases, so the field is removed rather than discouraged.
 */
export function observedOf(response: HttpResponse): ObservedResponse {
  return {
    headers: response.headers,
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  };
}
